import bus from "@src/services/EventBus"
import {
  JOURNEY_COMPLETED_EVENT,
  type JourneyCompletedPayload,
} from "@src/services/eventBusContract"
import { trackEvent } from "@src/utils/trackEvent"

type Stage = "started" | "submitted" | "completed" | "failed"
type FieldRule =
  | "string"
  | "string?"
  | "decimal"
  | "number"
  | "number?"
  | "positive"
  | "fraction"
  | "boolean?"
  | readonly string[]
type FieldSchema = Record<string, FieldRule>

// One compact contract drives both TypeScript and runtime projection.
const RULES = {
  auth: {
    surface: "login",
    monetary: false,
    operationCompleted: false,
    facts: {
      auth_method: ["wallet", "passkey"],
      auth_action: ["connect", "create", "sign_in"],
      chain: "string?",
      wallet_id: "string?",
    },
    submitted: false,
    completed: { duration_ms: "number", account_created: "boolean?" },
    failed: {
      failure_stage: ["connection", "ceremony", "verification"],
      failure_reason: [
        "cancelled",
        "rejected",
        "credential_not_found",
        "popup_blocked",
        "timeout",
        "not_supported",
        "network_error",
        "backend_error",
        "technical_error",
        "verification_rejected",
        "unknown",
      ],
    },
  },
  deposit: {
    surface: "deposit",
    monetary: true,
    operationCompleted: true,
    facts: {
      asset_id: "string",
      asset_symbol: "string",
      amount: "decimal",
      source_chain: "string",
    },
    submitted: true,
    completed: { completion_evidence: ["credited_balance_delta"] },
    failed: false,
  },
  swap: {
    surface: "swap",
    monetary: true,
    operationCompleted: true,
    facts: {
      input_asset_id: "string",
      input_asset_symbol: "string",
      output_asset_id: "string",
      output_asset_symbol: "string",
      amount_in: "decimal",
      amount_out: "decimal",
      source_chain: "string",
      destination_chain: "string",
      output_value_usd: "number?",
    },
    submitted: true,
    completed: {},
    failed: false,
  },
  withdraw: {
    surface: "withdraw",
    monetary: true,
    operationCompleted: true,
    facts: {
      asset_id: "string",
      asset_symbol: "string",
      amount: "decimal",
      source_chain: "string",
      destination_chain: "string",
    },
    submitted: true,
    completed: {},
    failed: false,
  },
  confidential_account_move: {
    surface: "transfer",
    monetary: false,
    operationCompleted: true,
    facts: {
      direction: ["to_confidential", "to_main"],
    },
    submitted: true,
    completed: {},
    failed: false,
  },
  earn_vault_access: {
    surface: "earn",
    monetary: false,
    operationCompleted: false,
    facts: {
      required_mode: ["confidential"],
      product_type: ["vault"],
    },
    submitted: false,
    completed: false,
    failed: false,
  },
  earn_stake: {
    surface: "earn",
    monetary: true,
    operationCompleted: true,
    facts: {
      asset_id: "string",
      asset_symbol: "string",
      amount: "decimal",
      market_id: "string",
      has_existing_position: "boolean?",
    },
    submitted: true,
    completed: {},
    failed: {
      failure_stage: ["signing", "submission"],
      failure_reason: [
        "user_cancelled",
        "wallet_rejected",
        "provider_rejected",
      ],
    },
  },
  earn_unstake: {
    surface: "earn",
    monetary: true,
    operationCompleted: true,
    facts: {
      asset_id: "string",
      asset_symbol: "string",
      amount: "decimal",
      market_id: "string",
      has_existing_position: "boolean?",
    },
    submitted: true,
    completed: {},
    failed: {
      failure_stage: ["signing", "submission"],
      failure_reason: [
        "user_cancelled",
        "wallet_rejected",
        "provider_rejected",
      ],
    },
  },
  earn_claim: {
    surface: "earn",
    monetary: true,
    operationCompleted: false,
    facts: {
      asset_id: "string",
      asset_symbol: "string",
      amount: "decimal",
      market_id: "string",
    },
    submitted: false,
    completed: {},
    failed: {
      failure_stage: ["claim"],
      failure_reason: ["nothing_to_claim"],
    },
  },
  perps_signer_setup: {
    surface: "perps",
    monetary: false,
    operationCompleted: false,
    facts: {
      setup_method: ["create", "discover"],
      setup_reason: [
        "generic",
        "wallet_incompatible",
        "passkey_unavailable",
        "persistence_failed",
      ],
    },
    submitted: false,
    completed: {
      origin: [
        "evm",
        "passkey_session",
        "passkey_linked",
        "passkey_new",
        "passkey_discovered",
      ],
    },
    failed: {
      failure_stage: ["ceremony", "setup"],
      failure_reason: ["user_cancelled", "terminal_rejection"],
    },
  },
  perps_fund: {
    surface: "perps",
    monetary: true,
    operationCompleted: true,
    facts: {
      asset_id: "string",
      asset_symbol: "string",
      amount: "decimal",
      source_chain: "string",
      destination_chain: "string",
    },
    submitted: true,
    completed: {},
    failed: false,
  },
  perps_order: {
    surface: "perps",
    monetary: true,
    operationCompleted: true,
    facts: {
      market: "string",
      side: ["long", "short"],
      order_type: ["market", "limit"],
      margin_usd: "number",
      notional_usd: "number",
      leverage: "positive",
    },
    submitted: false,
    completed: {
      result: ["filled", "resting"],
      protection_status: ["attached", "failed", "not_requested"],
    },
    failed: false,
  },
  perps_position_close: {
    surface: "perps",
    monetary: true,
    operationCompleted: true,
    facts: {
      market: "string",
      side: ["long", "short"],
      close_fraction: "fraction",
      notional_usd: "number",
    },
    submitted: false,
    completed: { result: ["filled", "resting"] },
    failed: false,
  },
  perps_withdraw: {
    surface: "perps",
    monetary: true,
    operationCompleted: true,
    facts: {
      asset_symbol: ["USDC"],
      mode: ["public", "confidential"],
      net_value_usd: "number",
      fee_usd: "number",
    },
    submitted: true,
    completed: {},
    failed: false,
  },
} as const satisfies Record<
  string,
  {
    surface: string
    monetary: boolean
    operationCompleted: boolean
    facts: FieldSchema
    submitted: boolean
    completed: FieldSchema | false
    failed: FieldSchema | false
  }
>

export type Flow = keyof typeof RULES
export type AuthFailureStage =
  (typeof RULES)["auth"]["failed"]["failure_stage"][number]
export type AuthFailureReason =
  (typeof RULES)["auth"]["failed"]["failure_reason"][number]
type EnabledFlow<K extends "submitted" | "completed" | "failed"> = {
  [F in Flow]: (typeof RULES)[F][K] extends false ? never : F
}[Flow]
type OperationCompletedFlow = {
  [F in EnabledFlow<"completed">]: (typeof RULES)[F]["operationCompleted"] extends true
    ? F
    : never
}[EnabledFlow<"completed">]

type ValueOf<R extends FieldRule> = R extends readonly string[]
  ? R[number]
  : R extends "string" | "string?" | "decimal"
    ? string
    : R extends "boolean?"
      ? boolean
      : number
type OptionalKey<S extends FieldSchema> = {
  [K in keyof S]: S[K] extends `${string}?` ? K : never
}[keyof S]
type Shape<S extends FieldSchema> = {
  [K in Exclude<keyof S, OptionalKey<S>>]: ValueOf<S[K]>
} & { [K in OptionalKey<S>]?: ValueOf<S[K]> }

export type JourneyId = string & { readonly __journeyId: unique symbol }
export type Valuation =
  | Readonly<{
      valueUsd: number
      valueQuality: "estimated" | "quoted" | "denominated"
    }>
  | Readonly<{ valueUsd: null; valueQuality: "unavailable" }>
export type JourneyProvenance<F extends Flow = Flow> = F extends Flow
  ? Readonly<{
      journeyId: JourneyId
      flow: F
      privacy: "public" | "confidential"
      valuation: (typeof RULES)[F]["monetary"] extends true ? Valuation : null
    }>
  : never
type OperationClaim = Readonly<{
  namespace:
    | "chain_tx"
    | "intent"
    | "deposit_credit"
    | "perps_cloid"
    | "one_click"
    | "claim"
  key: string
}>

type Common<F extends Flow> = {
  journey: JourneyProvenance<F>
  surface: (typeof RULES)[F]["surface"]
}
type Facts<F extends Flow> = Shape<(typeof RULES)[F]["facts"]>
type StageFacts<
  F extends Flow,
  K extends "completed" | "failed",
> = (typeof RULES)[F][K] extends FieldSchema
  ? Shape<(typeof RULES)[F][K]>
  : never

export type StartedEvent = {
  [F in Flow]: Common<F> & Facts<F>
}[Flow]
export type SubmittedEvent = {
  [F in EnabledFlow<"submitted">]: Common<F> &
    Facts<F> & { operation: OperationClaim }
}[EnabledFlow<"submitted">]
export type CompletedEvent = {
  [F in EnabledFlow<"completed">]: Common<F> &
    Facts<F> &
    StageFacts<F, "completed"> &
    (F extends OperationCompletedFlow
      ? { operation: OperationClaim }
      : { operation?: never })
}[EnabledFlow<"completed">]
export type FailedEvent = {
  [F in EnabledFlow<"failed">]: Common<F> &
    Facts<F> &
    StageFacts<F, "failed"> & { operation?: never }
}[EnabledFlow<"failed">]

type Primitive = string | number | boolean | null
type Payload = Record<string, Primitive>
type Privacy = "public" | "confidential"
type Claim = true | Privacy | "completed" | "failed"
const claims = new Map<string, Claim>()
let fallbackId = 0

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

function includes<T>(values: readonly T[], value: unknown): value is T {
  return values.includes(value as T)
}

function validString(value: unknown): value is string {
  return (
    typeof value === "string" && value.trim().length > 0 && value.length <= 512
  )
}

function hasExactOwnKeys(
  value: Record<string, unknown>,
  expected: readonly string[]
): boolean {
  const keys = Object.keys(value)
  return (
    keys.length === expected.length &&
    keys.every((field) => expected.includes(field))
  )
}

function validOperation(value: unknown): value is OperationClaim {
  return (
    isRecord(value) &&
    hasExactOwnKeys(value, ["namespace", "key"]) &&
    includes(
      [
        "chain_tx",
        "intent",
        "deposit_credit",
        "perps_cloid",
        "one_click",
        "claim",
      ],
      value.namespace
    ) &&
    validString(value.key)
  )
}

function validField(rule: FieldRule, value: unknown): boolean {
  if (typeof rule !== "string") return includes(rule, value)
  const kind = rule.endsWith("?") ? rule.slice(0, -1) : rule
  if (kind === "string") return validString(value)
  if (kind === "decimal") {
    return (
      typeof value === "string" &&
      value.length <= 512 &&
      /^(?:0|[1-9]\d*)(?:\.\d+)?$/.test(value)
    )
  }
  if (kind === "boolean") return typeof value === "boolean"
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
    return false
  }
  if (kind === "positive") return value > 0
  if (kind === "fraction") return value > 0 && value <= 1
  return true
}

function valuation(rule: (typeof RULES)[Flow], value: unknown): Payload | null {
  if (!rule.monetary) return value === null ? {} : null
  if (
    !isRecord(value) ||
    !hasExactOwnKeys(value, ["valueUsd", "valueQuality"])
  ) {
    return null
  }
  if (value.valueUsd === null && value.valueQuality === "unavailable") {
    return { value_usd: null, value_quality: "unavailable" }
  }
  if (
    typeof value.valueUsd === "number" &&
    Number.isFinite(value.valueUsd) &&
    value.valueUsd >= 0 &&
    includes(["estimated", "quoted", "denominated"], value.valueQuality)
  ) {
    return {
      value_usd: value.valueUsd,
      value_quality: value.valueQuality,
      value: value.valueUsd,
      currency: "USD",
    }
  }
  return null
}

function project(stage: Stage, input: unknown) {
  if (
    !isRecord(input) ||
    !Object.hasOwn(input, "journey") ||
    !Object.hasOwn(input, "surface") ||
    !isRecord(input.journey)
  ) {
    return null
  }
  const journey = input.journey
  if (
    !hasExactOwnKeys(journey, ["journeyId", "flow", "privacy", "valuation"]) ||
    !validString(journey.journeyId) ||
    typeof journey.flow !== "string" ||
    !Object.hasOwn(RULES, journey.flow) ||
    !includes(["public", "confidential"], journey.privacy)
  ) {
    return null
  }
  const flow = journey.flow as Flow
  const rule = RULES[flow]
  const stageSchema =
    stage === "submitted"
      ? rule.submitted
      : stage === "completed"
        ? rule.completed
        : stage === "failed"
          ? rule.failed
          : true
  if (stageSchema === false || input.surface !== rule.surface) return null

  const operationRequired =
    stage === "submitted" || (stage === "completed" && rule.operationCompleted)
  if (operationRequired !== Object.hasOwn(input, "operation")) return null
  if (operationRequired && !validOperation(input.operation)) return null

  const schema: FieldSchema = {
    ...rule.facts,
    ...(typeof stageSchema === "object" ? stageSchema : {}),
  }
  const allowed = new Set([
    "journey",
    "surface",
    ...Object.keys(schema),
    ...(operationRequired ? ["operation"] : []),
  ])
  if (Object.keys(input).some((field) => !allowed.has(field))) return null

  const facts: Payload = {}
  for (const [field, fieldRule] of Object.entries(schema)) {
    const isOwnField = Object.hasOwn(input, field)
    const value = input[field]
    if (
      !isOwnField &&
      typeof fieldRule === "string" &&
      fieldRule.endsWith("?")
    ) {
      continue
    }
    if (!isOwnField || !validField(fieldRule, value)) return null
    facts[field] = value as Primitive
  }
  const value = valuation(rule, journey.valuation)
  if (value === null) return null

  return {
    journeyId: journey.journeyId,
    privacy: journey.privacy,
    operation: operationRequired
      ? (input.operation as OperationClaim)
      : undefined,
    payload: {
      schema_version: 1,
      event_id: `${journey.journeyId}:${stage}`,
      journey_id: journey.journeyId,
      flow,
      surface: rule.surface,
      ...value,
      ...facts,
    } satisfies Payload,
  }
}

function key(...parts: string[]) {
  return JSON.stringify(parts)
}

function emit(stage: Stage, input: unknown): void {
  const event = project(stage, input)
  if (event === null) return

  const aliases: Array<[string, Claim]> = [
    [key("stage", "journey", event.journeyId, stage), true],
  ]
  if (event.operation) {
    aliases.push([
      key(
        "stage",
        "operation",
        event.operation.namespace,
        event.operation.key,
        stage
      ),
      true,
    ])
  }
  if (stage === "completed" || stage === "failed") {
    aliases.push([key("terminal", "journey", event.journeyId), stage])
    if (event.operation) {
      aliases.push([
        key(
          "terminal",
          "operation",
          event.operation.namespace,
          event.operation.key
        ),
        stage,
      ])
    }
  }

  const privacyKeys = [
    key("privacy", "journey", event.journeyId),
    ...(event.operation
      ? [
          key(
            "privacy",
            "operation",
            event.operation.namespace,
            event.operation.key
          ),
        ]
      : []),
  ]
  const frozenPrivacy: Privacy =
    event.privacy === "confidential" ||
    privacyKeys.some((privacyKey) => claims.get(privacyKey) === "confidential")
      ? "confidential"
      : "public"
  const existing = aliases.filter(([alias]) => claims.has(alias))
  const conflict = existing.some(([alias, next]) => {
    const current = claims.get(alias)
    return (
      next !== true &&
      current !== true &&
      current !== "public" &&
      current !== "confidential" &&
      next !== current
    )
  })
  if (conflict) return

  for (const [alias, value] of aliases) claims.set(alias, value)
  for (const privacyKey of privacyKeys) claims.set(privacyKey, frozenPrivacy)
  if (existing.length > 0 || frozenPrivacy === "confidential") return
  trackEvent(`flow_${stage}`, event.payload)
  if (stage === "completed") {
    bus.emit(JOURNEY_COMPLETED_EVENT, {
      flow: event.payload.flow,
      journey_id: event.journeyId,
      ...(event.operation ? { transaction_id: event.operation.key } : {}),
    } satisfies JourneyCompletedPayload)
  }
}

function safelyEmit(stage: Stage, input: unknown): void {
  try {
    emit(stage, input)
  } catch {
    // Analytics must never affect product behavior.
  }
}

function newId(): JourneyId {
  try {
    return globalThis.crypto.randomUUID() as JourneyId
  } catch {
    fallbackId += 1
    return `local-${Date.now().toString(36)}-${fallbackId}` as JourneyId
  }
}

export const journeyAnalytics = Object.freeze({
  newId,
  started: (event: StartedEvent): void => safelyEmit("started", event),
  submitted: (event: SubmittedEvent): void => safelyEmit("submitted", event),
  completed: (event: CompletedEvent): void => safelyEmit("completed", event),
  failed: (event: FailedEvent): void => safelyEmit("failed", event),
})
